import { ZatcaIntegrationStatus } from '../models/ZatcaIntegration'

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

export type ZatcaToneKey = 'disabled' | 'active' | 'soon' | 'expired'

export type ZatcaTone = {
  key: ZatcaToneKey
  bg: string
  border: string
  text: string
  icon: string
}

const TONES: Record<ZatcaToneKey, ZatcaTone> = {
  disabled: { key: 'disabled', bg: '#f2f4f5', border: '#dfe3e6', text: '#6b7a83', icon: '#9aa5ab' },
  active: { key: 'active', bg: '#e7f6ea', border: '#28C76F', text: '#1d8a4b', icon: '#28C76F' },
  soon: { key: 'soon', bg: '#fff8e0', border: '#FFC20F', text: '#8a6d00', icon: '#c79600' },
  expired: { key: 'expired', bg: '#fdecec', border: '#f6c9c9', text: '#a32c2d', icon: '#EA5455' },
}

/**
 * Derives the visual tone (colors, urgency) purely from stored data: isIntegrated,
 * lifecycleStatus, and renewalDue (AVTAX's certificateExpiresAtUtc) — never hardcoded, never a
 * calculated expiry.
 */
export const getZatcaStatusTone = (status: ZatcaIntegrationStatus): ZatcaTone => {
  if (!status.isIntegrated) {
    return TONES.disabled
  }

  const isLifecycleActive = !status.status || status.status === 'Active'

  // An unexpected/abnormal lifecycleStatus (anything other than "Active") must never fall
  // through to the healthy/green tone — that would show a contradictory "all good" pill for a
  // company whose EGS Unit AVTAX itself reports as not active.
  if (!isLifecycleActive) {
    return TONES.expired
  }

  const dueMs = status.renewalDue ? new Date(status.renewalDue).getTime() - Date.now() : null
  const expired = dueMs !== null && dueMs <= 0
  const soon = dueMs !== null && dueMs > 0 && dueMs < THIRTY_DAYS_MS

  return expired ? TONES.expired : soon ? TONES.soon : TONES.active
}

/** Whole days remaining until renewalDue, rounded up so "less than a day left" still reads as 1, not 0. */
export const getZatcaRenewalDaysLeft = (status: ZatcaIntegrationStatus): number | null => {
  if (!status.renewalDue) {
    return null
  }
  const dueMs = new Date(status.renewalDue).getTime() - Date.now()
  return dueMs > 0 ? Math.ceil(dueMs / (24 * 60 * 60 * 1000)) : 0
}
